import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { IngredientV2 } from '@llm-macro-tracker/shared';
import { InferredIngredientBadge } from '@/components/InferredIngredientBadge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2, Edit2, Check, X } from 'lucide-react';

interface IngredientListProps {
  ingredients: IngredientV2[];
  totalCalories: number;
  onUpdateIngredient: (index: number, updatedIngredient: IngredientV2) => void;
  onDeleteIngredient: (index: number) => void;
}

export function IngredientList({
  ingredients,
  totalCalories,
  onUpdateIngredient,
  onDeleteIngredient,
}: IngredientListProps) {
  const MIN_EMOJI_WIDTH = 5;
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingAmount, setEditingAmount] = useState<string>('');
  const [amountError, setAmountError] = useState<string | null>(null);

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditingAmount(ingredients[index].amount.toFixed(1));
    setAmountError(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingAmount('');
    setAmountError(null);
  };

  const handleSaveEdit = (index: number) => {
    const currentIngredient = ingredients[index];
    const newAmount = parseFloat(parseFloat(editingAmount).toFixed(1));

    if (isNaN(newAmount) || newAmount <= 0) {
      setAmountError('Amount must be greater than 0');
      return;
    }

    const amountRatio = newAmount / currentIngredient.amount;
    const updatedIngredient: IngredientV2 = {
      ...currentIngredient,
      amount: newAmount,
      calories: Math.round(currentIngredient.calories * amountRatio),
      protein: Math.round(currentIngredient.protein * amountRatio * 10) / 10,
      carbs: Math.round(currentIngredient.carbs * amountRatio * 10) / 10,
      fat: Math.round(currentIngredient.fat * amountRatio * 10) / 10,
    };

    onUpdateIngredient(index, updatedIngredient);
    setEditingIndex(null);
    setEditingAmount('');
    setAmountError(null);
  };

  // New helper function to format the amount
  const formatAmount = (amount: number) => {
    return Number.isInteger(amount) ? amount.toString() : amount.toFixed(1);
  };

  // Function to determine if there's enough space for the emoji
  const showEmoji = (width: number) => width >= MIN_EMOJI_WIDTH;

  return (
    <div className="space-y-2">
      {ingredients.map((ingredient, index) => {
        const caloriePercentage = (ingredient.calories / totalCalories) * 100;
        const proteinCalories = ingredient.protein * 4;
        const carbCalories = ingredient.carbs * 4;
        const fatCalories = ingredient.fat * 9;

        const proteinWidth = (proteinCalories / ingredient.calories) * 100;
        const carbsWidth = (carbCalories / ingredient.calories) * 100;
        const fatWidth = (fatCalories / ingredient.calories) * 100;

        // Calculate the actual width of each macro in the context of the whole meal
        const actualProteinWidth = (proteinWidth / 100) * caloriePercentage;
        const actualCarbsWidth = (carbsWidth / 100) * caloriePercentage;
        const actualFatWidth = (fatWidth / 100) * caloriePercentage;

        return (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-3">
              <div className="flex flex-wrap items-start justify-between mb-2">
                <div className="flex-1 min-w-0 mr-2 overflow-hidden">
                  <div className="flex items-center mb-0.5">
                    <InferredIngredientBadge isInferred={ingredient.isInferred} />
                  </div>
                  <span className="font-medium text-sm break-words">{ingredient.name}</span>
                  {editingIndex === index ? (
                    <div className="flex flex-col mt-1">
                      <div className="flex items-center">
                        <Input
                          type="number"
                          value={editingAmount}
                          onChange={(e) => {
                            setEditingAmount(e.target.value);
                            setAmountError(null);
                          }}
                          step="0.1"
                          min="0.1"
                          className="w-20 mr-2 text-xs"
                        />
                        <span className="text-xs mr-2">{ingredient.unit}</span>
                        <Button size="sm" onClick={() => handleSaveEdit(index)} className="mr-1">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {amountError && (
                        <span className="text-xs text-red-500 mt-1">{amountError}</span>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-600 flex items-center">
                      {formatAmount(ingredient.amount)} {ingredient.unit}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(index)}
                        className="ml-1"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center mt-2 sm:mt-0">
                  <span className="text-sm text-gray-500 whitespace-nowrap mr-2">
                    {ingredient.calories} cal
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => onDeleteIngredient(index)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
              <div className="w-full h-6 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full flex" style={{ width: `${caloriePercentage}%` }}>
                  <div
                    className="bg-red-100 h-full flex items-center justify-center"
                    style={{ width: `${proteinWidth}%` }}
                  >
                    {showEmoji(actualProteinWidth) && (
                      <span className="text-red-800 text-xs">🥩</span>
                    )}
                  </div>
                  <div
                    className="bg-yellow-100 h-full flex items-center justify-center"
                    style={{ width: `${carbsWidth}%` }}
                  >
                    {showEmoji(actualCarbsWidth) && (
                      <span className="text-yellow-800 text-xs">🍞</span>
                    )}
                  </div>
                  <div
                    className="bg-green-100 h-full flex items-center justify-center"
                    style={{ width: `${fatWidth}%` }}
                  >
                    {showEmoji(actualFatWidth) && (
                      <span className="text-green-800 text-xs">🥑</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-between text-xs mt-1 text-gray-600">
                <span className="mr-2">{ingredient.protein}g protein</span>
                <span className="mr-2">{ingredient.carbs}g carbs</span>
                <span>{ingredient.fat}g fat</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
